<template>
  <div
    class="content_holder v-dialog--scrollable"
    :class="{
      'v-dialog--fullscreen': fullscreen,
      'pt-2': showProgressBar && !loading && !$vuetify.breakpoint.smAndDown
    }"
  >
    <v-progress-linear
      v-if="showProgressBar && !loading && !$vuetify.breakpoint.smAndDown"
      :value="progressBarValue"
      height="28"
      class="progress_bar"
    />
    <v-card :elevation="0">
      <v-card-title
        v-if="($slots.title || title || showCloseButton) && headerType !== 'none'"
        class="px-4 px-md-10 pt-4 pt-md-10 pb-4 dialog-header"
        :class="{
          'border-bottom':
            showTopBorder &&
            (!$vuetify.breakpoint.smAndDown || ($vuetify.breakpoint.smAndDown && !showProgressBar)),
          'white-bg': showTopBorder,
          'information-title': headerType === 'information'
        }"
      >
        <DialogHeader
          v-if="!loading"
          :show-back-arrow="showBackArrow"
          :show-close="showCloseButton"
          :title-left="titleLeft"
          :left-icon="headerType !== 'information' ? leftHeaderIcon : ''"
          @clickBack="$emit('clickBack')"
          @close="$emit('close')"
        >
          <template
            v-if="
              (headerType === 'information' && showTopBorder) ||
              (headerType !== 'information' &&
                (showTitleBeforeScroll || (!showTitleBeforeScroll && showTopBorder)))
            "
          >
            <span v-html="title"></span>
            <slot name="title"></slot>
          </template>
          <template v-if="$slots.leftIcon && headerType === 'navigation'" #leftIcon>
            <slot name="leftIcon" />
          </template>
        </DialogHeader>
        <v-progress-linear
          v-if="
            showProgressBar &&
            !loading &&
            $vuetify.breakpoint.smAndDown &&
            (headerType === 'navigation' || (headerType === 'information' && showTopBorder))
          "
          :value="progressBarValue"
          height="1"
          class="progress_bar_mobile"
        />
      </v-card-title>
      <v-card-text
        v-if="$slots.text"
        class="px-0 flex-column card-text dialog-main-content"
        :class="[
          {
            'py-0': headerType === 'none',
            'pt-2': $vuetify.breakpoint.smAndDown || (headerType === 'navigation' && !loading),
            'pt-6': leftHeaderIcon,
            'pt-10':
              !$vuetify.breakpoint.smAndDown &&
              ((headerType === 'information' && !loading) ||
                (title === undefined && !$slots.title && !$slots.leftIcon)),
            'pt-20':
              $vuetify.breakpoint.smAndDown &&
              !persistent &&
              ((headerType === 'information' && !loading) ||
                (title === undefined && !$slots.title && !$slots.leftIcon)),
            'pb-8': !loading && headerType !== 'none',
            'pb-md-2': !loading && ($slots.actions || buttonsInternal),
            'pb-md-10': !loading && !$slots.actions && !buttonsInternal && headerType !== 'none',
            'pb-10':
              (!buttonsInternal || !buttonsInternal.length) &&
              !$slots.actions &&
              headerType !== 'none',
            'text-center': headerType === 'information' && $vuetify.breakpoint.smAndDown
          },
          `justify-${contentJustify}`,
          contentDisplayClasses
        ]"
      >
        <div :class="[textClass, { 'loadingDialog d-flex align-center': loading }]">
          <Loading v-if="loading" class="py-8" />
          <div v-else>
            <template v-if="headerType === 'information'">
              <div v-if="leftHeaderIconType === 'animated'" class="icon_56">
                <component :is="leftHeaderIconComponent" />
              </div>
              <v-img
                v-else-if="leftHeaderIcon"
                :width="$vuetify.breakpoint.smAndDown ? 72 : 56"
                :height="$vuetify.breakpoint.smAndDown ? 72 : 56"
                class="col-auto"
                :class="{
                  'mt-6 mx-auto': $vuetify.breakpoint.smAndDown,
                  'mb-4 mb-md-6': !title
                }"
                :src="$asset(`img/${leftHeaderIcon}`)"
              />
              <template v-if="$slots.leftIcon">
                <slot name="leftIcon" />
              </template>
              <div
                v-if="title && title.length && showTitleInContent"
                :class="`mt-4 mt-md-6 ${titleBottomMargin} ${
                  $vuetify.breakpoint.smAndDown ? 'text-h1' : 'text-h2'
                }`"
                v-html="title"
              />
            </template>
            <slot name="text"></slot>
          </div>
        </div>
      </v-card-text>
      <v-card-actions
        v-if="!loading && ($slots.actions || buttonsInternal)"
        :class="{
          'border-top':
            ((buttonsInternal || $slots.actions) && showBottomBorder) ||
            $vuetify.breakpoint.smAndDown,
          'pt-4 pb-4 pb-md-10 px-4 px-md-10': !$slots.actions && buttonsInternal,
          'pa-0': $slots.actions
        }"
      >
        <v-row v-if="!$slots.actions && buttonsInternal" no-gutters>
          <v-col
            v-for="(button, index) in buttonsInternal"
            :key="index"
            :cols="$vuetify.breakpoint.smAndDown ? 12 : 12 / buttonsInternal.length"
            :class="{
              'pr-1': !$vuetify.breakpoint.smAndDown && index < buttonsInternal.length - 1,
              'pl-1': !$vuetify.breakpoint.smAndDown && index !== 0,
              'pb-2':
                $vuetify.breakpoint.smAndDown &&
                ((button.orderMobile === undefined && index < buttonsInternal.length - 1) ||
                  (button.orderMobile !== undefined &&
                    button.orderMobile !== buttonsInternal.length - 1))
            }"
            :order="
              $vuetify.breakpoint.smAndDown && button.orderMobile !== undefined
                ? button.orderMobile
                : null
            "
          >
            <SendButton
              v-if="button.type === 'sendButton'"
              v-bind="button.props"
              :button-text="button.text"
              :block="isButtonBlock(button)"
              :color="buttonColor(button)"
              v-on="button.events"
            />
            <v-btn
              v-else
              v-bind="button.props"
              :block="isButtonBlock(button)"
              :color="buttonColor(button)"
              v-on="button.events"
            >
              <span v-html="button.text" />
            </v-btn>
          </v-col>
        </v-row>
        <template v-else-if="$slots.actions">
          <slot name="actions"></slot>
        </template>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'DialogContent',
  props: {
    cardClass: {
      type: String,
      default: null
    },
    showBackArrow: {
      type: Boolean,
      required: true
    },
    titleLeft: {
      type: Boolean,
      required: true
    },
    leftHeaderIcon: {
      type: String,
      default: null
    },
    leftHeaderIconType: {
      validator(value) {
        return ['static', 'animated'].includes(value);
      },
      default: 'static'
    },
    leftHeaderIconComponent: {
      type: String,
      default: null
    },
    textClass: {
      type: String,
      required: true
    },
    showCloseButton: {
      type: Boolean,
      required: true
    },
    fullscreen: {
      type: Boolean,
      required: true
    },
    buttons: {
      type: Array,
      required: false
    },
    mobileButtons: {
      type: Array,
      required: false
    },
    headerType: {
      validator(value) {
        return ['navigation', 'information', 'none'].includes(value);
      },
      default: 'navigation'
    },
    dialogValue: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    showTitleInContent: {
      type: Boolean,
      required: true
    },
    showTitleBeforeScroll: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: false
    },
    showProgressBar: {
      type: Boolean,
      default: false
    },
    progressBarValue: {
      type: Number,
      required: false
    },
    titleBottomMargin: {
      type: String,
      default: 'mb-4'
    },
    persistent: {
      type: Boolean,
      default: false
    },
    contentJustify: {
      type: String,
      default: 'start'
    },
    contentDisplayClasses: {
      type: String,
      default: 'd-flex d-md-block'
    }
  },
  data() {
    return {
      showTopBorder: false,
      showBottomBorder: false
    };
  },
  computed: {
    buttonsInternal() {
      let buttonsTmp = this.buttons;
      if (this.mobileButtons && this.$vuetify.breakpoint.smAndDown) {
        buttonsTmp = this.mobileButtons;
      }
      if (buttonsTmp && buttonsTmp.length) {
        return buttonsTmp;
      }
      return null;
    }
  },
  watch: {
    dialogValue(newValue) {
      if (newValue) {
        this.setUpWatcher();
      } else {
        this.removeWatcher();
      }
    },
    loading() {
      this.$nextTick(() => {
        this.setUpWatcher();
      });
    },
    headerType() {
      this.removeWatcher();
      this.setUpWatcher();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setUpWatcher();
    });
  },
  beforeDestroy() {
    this.removeWatcher();
  },
  methods: {
    isButtonBlock(button) {
      return button?.props?.block || true;
    },
    buttonColor(button) {
      return button?.props?.color || 'primary';
    },
    setUpWatcher() {
      this.$nextTick(() => {
        let dialogContent = null;
        if (this.isMobileApp) {
          dialogContent = document.querySelector('.dialog-main-content');
        } else {
          dialogContent = document.querySelector('.v-dialog--active .dialog-main-content');
        }
        this.dialogScrollTop();
        if (dialogContent) {
          this.setUpBorders();
          dialogContent.addEventListener('scroll', this.setUpBorders);

          if (
            !this.$vuetify.breakpoint.smAndDown &&
            dialogContent.scrollHeight > dialogContent.clientHeight
          ) {
            dialogContent.classList.add('padding-right-scroll');
          } else {
            dialogContent.classList.remove('padding-right-scroll');
          }
        }
      });
    },

    removeWatcher() {
      const dialogContent = document.querySelectorAll('.dialog-main-content');
      for (let iter = 0; iter < dialogContent.length; iter++) {
        dialogContent[iter].removeEventListener('scroll', this.setUpBorders);
      }
    },

    setUpBorders() {
      let dialogContent = null;
      if (this.isMobileApp) {
        dialogContent = document.querySelector('.dialog-main-content');
      } else {
        dialogContent = document.querySelector('.v-dialog--active .dialog-main-content');
      }
      if (dialogContent) {
        this.showTopBorder =
          dialogContent.scrollTop >
          (this.headerType === 'information' ? (this.$vuetify.breakpoint.smAndDown ? 150 : 55) : 8);
        this.showBottomBorder =
          dialogContent.scrollTop + dialogContent.offsetHeight - dialogContent.scrollHeight < -2;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.content_holder {
  width: 100%;
  position: relative;

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    height: 100%;
  }

  .information-title {
    position: absolute;
    width: 100%;
    z-index: 3;
  }

  .border-bottom {
    border-bottom: $border-width solid $grey-lighten;
  }

  .border-top {
    border-top: $border-width solid $grey-lighten;
  }

  .information-title.white-bg {
    background: $white;
  }
}

.progress_bar {
  position: absolute;
  top: 0;
  left: 0;
  border-top-right-radius: map-get($rounded, 'xl');
  border-top-left-radius: map-get($rounded, 'xl');
}

.dialog-header {
  position: relative;

  .progress_bar_mobile {
    position: absolute;
    bottom: 0;
    left: 0;
  }
}

.loadingDialog {
  height: 100%;
}
</style>
